import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const TOOLS = [
  {
    id: 'text-to-game',
    icon: 'Gamepad2',
    title: 'Text to Game',
    desc: 'Generate a complete playable game from your description',
    color: '#6366F1',
    gradient: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
    badge: 'Popular',
    badgeColor: '#6366F1',
    category: 'Game',
  },
  {
    id: 'prompt-to-mini-game',
    icon: 'Zap',
    title: 'Mini Game Creator',
    desc: 'Quick arcade or puzzle mini-game from a single sentence',
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, #F59E0B, #EF4444)',
    badge: 'Fast',
    badgeColor: '#F59E0B',
    category: 'Game',
  },
  {
    id: 'script-to-video',
    icon: 'Video',
    title: 'Script to Video',
    desc: 'Turn scripts into cinematic video trailers',
    color: '#10B981',
    gradient: 'linear-gradient(135deg, #10B981, #06B6D4)',
    badge: null,
    badgeColor: null,
    category: 'Video',
  },
  {
    id: 'image-to-video',
    icon: 'ImagePlay',
    title: 'Image to Video',
    desc: 'Animate a single image with cinematic effects',
    color: '#06B6D4',
    gradient: 'linear-gradient(135deg, #06B6D4, #6366F1)',
    badge: 'New',
    badgeColor: '#06B6D4',
    category: 'Video',
  },
  {
    id: 'music-to-video',
    icon: 'Music',
    title: 'Music to Video',
    desc: 'Generate synchronized visuals from music',
    color: '#F43F5E',
    gradient: 'linear-gradient(135deg, #F43F5E, #EC4899)',
    badge: null,
    badgeColor: null,
    category: 'Video',
  },
  {
    id: 'lip-sync',
    icon: 'Mic',
    title: 'Lip Sync Character',
    desc: 'Animate faces with voice — talking character videos',
    color: '#EC4899',
    gradient: 'linear-gradient(135deg, #EC4899, #F43F5E)',
    badge: 'Hot',
    badgeColor: '#EC4899',
    category: 'Character',
  },
  {
    id: 'text-to-music',
    icon: 'Music2',
    title: 'AI Music Generator',
    desc: 'Generate original copyright-safe music in any style',
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
    badge: 'New',
    badgeColor: '#8B5CF6',
    category: 'Music',
  },
  {
    id: 'text-to-animation',
    icon: 'Film',
    title: 'Animation Generator',
    desc: 'Create smooth animations from text descriptions',
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, #F59E0B, #10B981)',
    badge: null,
    badgeColor: null,
    category: 'Animation',
  },
  {
    id: 'talking-avatar',
    icon: 'UserCircle',
    title: 'Talking Avatar',
    desc: 'Create a personalized talking avatar from your photo',
    color: '#6366F1',
    gradient: 'linear-gradient(135deg, #6366F1, #EC4899)',
    badge: 'Beta',
    badgeColor: '#6366F1',
    category: 'Character',
  },
];

const CATEGORIES = ['All', 'Game', 'Video', 'Music', 'Animation', 'Character'];

export default function ToolGallery({ onSelectTool, selectedTool }) {
  const [activeCategory, setActiveCategory] = React.useState('All');

  const filtered = activeCategory === 'All' ? TOOLS : TOOLS?.filter((t) => t?.category === activeCategory);

  return (
    <div className="flex flex-col gap-4">
      {/* Category filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        {CATEGORIES?.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-full border transition-all"
            style={{
              borderColor: activeCategory === cat ? 'var(--color-primary)' : 'var(--color-border)',
              background: activeCategory === cat ? 'rgba(99,102,241,0.1)' : 'var(--color-card)',
              color: activeCategory === cat ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
              fontFamily: 'var(--font-caption)',
            }}
          >
            {cat}
          </button>
        ))}
      </div>
      {/* Tool grid */}
      <div className="grid grid-cols-1 gap-2">
        {filtered?.map((tool) => {
          const active = selectedTool === tool?.id;
          return (
            <button
              key={tool?.id}
              onClick={() => onSelectTool?.(tool?.id)}
              className="flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-200 hover:shadow-md"
              style={{
                borderColor: active ? tool?.color : 'var(--color-border)',
                background: active ? `${tool?.color}10` : 'var(--color-card)',
                boxShadow: active ? `0 0 0 2px ${tool?.color}30` : 'none',
                transform: active ? 'translateX(2px)' : 'translateX(0)',
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: active ? tool?.gradient : `${tool?.color}18` }}
              >
                <Icon name={tool?.icon} size={18} color={active ? '#fff' : tool?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold" style={{ color: active ? tool?.color : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{tool?.title}</p>
                  {tool?.badge && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${tool?.badgeColor}20`, color: tool?.badgeColor, fontFamily: 'var(--font-caption)', fontSize: 9 }}>
                      {tool?.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{tool?.desc}</p>
              </div>
              <Icon name="ChevronRight" size={14} color={active ? tool?.color : 'var(--color-muted-foreground)'} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
